import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DataProvider } from "./contexts/DataContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Pipeline from "./pages/Pipeline";
import Inbox from "./pages/Inbox";
import Suppliers from "./pages/Suppliers";
import Tasks from "./pages/Tasks";
import Automations from "./pages/Automations";
import MessagingQueue from "./pages/MessagingQueue";
import StalledQuoteImport from "./pages/StalledQuoteImport";
import EstimatorIntakeLog from "./pages/EstimatorIntakeLog";
import EmailLog from "./pages/EmailLog";
import StageAutomations from "./pages/StageAutomations";
import WorkflowBuilder from "./pages/WorkflowBuilder";
import Settings from "./pages/Settings";
import Notifications from "./pages/Notifications";
import Today from "./pages/Today";
import Privacy from "./pages/Privacy";
import Quotes from "./pages/Quotes";
import QuoteBuilder from "./pages/QuoteBuilder";
import QuoteTemplates from "./pages/QuoteTemplates";
import PriceList from "./pages/PriceList";
import Analytics from "./pages/Analytics";
import InspectionReports from "./pages/InspectionReports";
import MeasurementCalculator from "./pages/MeasurementCalculator";
import Schedule from "./pages/Schedule";
import PipelineHealth from "./pages/PipelineHealth";
import Layout from "./components/Layout";
import JoinPage from "./pages/JoinPage";
import PublicQuoteView from "./pages/PublicQuoteView";
import PublicFeedbackView from "./pages/PublicFeedbackView";
import CounterSignPage from "./pages/CounterSignPage";
import PublicInspectionView from "./pages/PublicInspectionView";
import PublicInvoicePay from "./pages/PublicInvoicePay";
import PaymentResult from "./pages/PaymentResult";
import CustomerPortal from "./pages/CustomerPortal";
import ReferralForm from "./pages/ReferralForm";
import Trash from "./pages/Trash";
import Billing from "./pages/Billing";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F4C81] border-t-transparent"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/public/quote/:quoteId" element={<PublicQuoteView />} />
      <Route path="/q/:quoteId" element={<PublicQuoteView />} />
      <Route path="/public/quote/:quoteId/counter-sign" element={<CounterSignPage />} />
      <Route path="/public/inspection/:reportId" element={<PublicInspectionView />} />
      <Route path="/pay/:invoiceId" element={<PublicInvoicePay />} />
      <Route path="/i/:invoiceId" element={<PublicInvoicePay />} />
      <Route path="/portal/:token" element={<CustomerPortal />} />
      <Route path="/p/:token" element={<CustomerPortal />} />
      <Route path="/refer/:code" element={<ReferralForm />} />
      <Route path="/r/:code" element={<ReferralForm />} />
      <Route path="/feedback/:projectId" element={<PublicFeedbackView />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult />} />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/today" element={
        <ProtectedRoute>
          <Layout>
            <Today />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/contacts" element={
        <ProtectedRoute>
          <Layout>
            <Contacts />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/contacts/:id" element={
        <ProtectedRoute>
          <Layout>
            <ContactDetail />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute>
          <Layout>
            <Projects />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/projects/:id" element={
        <ProtectedRoute>
          <Layout>
            <ProjectDetail />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pipeline" element={
        <ProtectedRoute>
          <Layout>
            <Pipeline />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/inbox" element={
        <ProtectedRoute>
          <Layout>
            <Inbox />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/suppliers" element={
        <ProtectedRoute>
          <Layout>
            <Suppliers />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/tasks" element={
        <ProtectedRoute>
          <Layout>
            <Tasks />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/automations" element={
        <ProtectedRoute>
          <Layout>
            <Automations />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/messaging-queue" element={
        <ProtectedRoute>
          <Layout>
            <MessagingQueue />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/imports/stalled-roof-quotes" element={
        <ProtectedRoute>
          <Layout>
            <StalledQuoteImport />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/diagnostics/estimator-intake" element={
        <ProtectedRoute>
          <Layout>
            <EstimatorIntakeLog />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/automations/build/:id" element={
        <ProtectedRoute>
          <WorkflowBuilder />
        </ProtectedRoute>
      } />
      <Route path="/email-log" element={
        <ProtectedRoute>
          <Layout>
            <EmailLog />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/stage-automations" element={
        <ProtectedRoute>
          <Layout>
            <StageAutomations />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Layout>
            <Notifications />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/trash" element={
        <ProtectedRoute>
          <Layout>
            <Trash />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/quotes" element={
        <ProtectedRoute>
          <Layout>
            <Quotes />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/quotes/new" element={
        <ProtectedRoute>
          <Layout>
            <QuoteBuilder />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/quotes/:id" element={
        <ProtectedRoute>
          <Layout>
            <QuoteBuilder />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/quote-templates" element={
        <ProtectedRoute>
          <Layout>
            <QuoteTemplates />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/price-list" element={
        <ProtectedRoute>
          <Layout>
            <PriceList />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <Layout>
            <Analytics />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/inspections" element={
        <ProtectedRoute>
          <Layout>
            <InspectionReports />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/measurements" element={
        <ProtectedRoute>
          <Layout>
            <MeasurementCalculator />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/measurements/:reportId" element={
        <ProtectedRoute>
          <Layout>
            <MeasurementCalculator />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute>
          <Layout>
            <Billing />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/schedule" element={
        <ProtectedRoute>
          <Layout>
            <Schedule />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pipeline-health" element={
        <ProtectedRoute>
          <Layout>
            <PipelineHealth />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DataProvider>
          <AppRoutes />
          <Toaster 
            position="bottom-right" 
            richColors 
            closeButton
            toastOptions={{
              style: { minHeight: '44px' }
            }}
          />
        </DataProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
